import { useIntl } from '@cmg/i18n';
import capitalize from 'lodash/capitalize';
import React, { useMemo } from 'react';

import { isIPOType } from '../../../../common/util/offering/offeringTypeHelpers';
import {
  MdlIndicationLimitType,
  MdlIndicationPricingType,
  MdlIndicationUnitType,
  OfferingType,
} from '../../../../graphql';
import { InterestLevelFormValues } from '../interest-levels-wrapper/InterestLevelsWrapper.model';
import { limitTypeOptions } from './LimitPriceInput.model';
import {
  StyledLimitPriceContainer,
  StyledLimitPriceCurrencyInputField,
  StyledLimitPricePercentInputField,
  StyledLimitPriceSelectField,
} from './LimitPriceInput.styles';

type Props = {
  interestLevel: InterestLevelFormValues<
    MdlIndicationUnitType,
    MdlIndicationPricingType,
    MdlIndicationLimitType
  >;
  offeringType: OfferingType;
  isEditing?: boolean;
  isSubmitting?: boolean;
  baseName: string;
  handleChangeLimitType: (limitType: MdlIndicationLimitType) => void;
};

const LimitPriceInput: React.FC<Props> = ({
  interestLevel,
  offeringType,
  isEditing = false,
  isSubmitting = false,
  baseName,
  handleChangeLimitType,
}) => {
  const intl = useIntl();

  const options = useMemo(() => {
    const isIPOOfferingType = isIPOType(offeringType);
    return limitTypeOptions.filter(option => {
      // Only show the "PRICE" limit type option if the offering type is IPO
      return (
        option.value !== MdlIndicationLimitType.Null &&
        (!isIPOOfferingType || option.value === MdlIndicationLimitType.Price)
      );
    });
  }, [offeringType]);

  if (interestLevel.pricingType !== MdlIndicationPricingType.Limit) {
    return null;
  }

  if (!isEditing) {
    return (
      <StyledLimitPriceContainer>
        {interestLevel.limitType && `${capitalize(interestLevel.limitType)} `}
        {interestLevel.limitPrice &&
          intl.formatNumber(interestLevel.limitPrice, { format: 'currency' })}
        {interestLevel.limitPercentage &&
          intl.formatNumber(interestLevel.limitPercentage, {
            format: 'percent',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
      </StyledLimitPriceContainer>
    );
  }

  const baseProps = {
    disabled: isSubmitting,
    fullWidth: true,
    required: true,
  };

  return (
    <StyledLimitPriceContainer>
      <StyledLimitPriceSelectField
        name={`${baseName}.limitType`}
        placeholder="Select..."
        disabled={isSubmitting}
        options={options}
        fullWidth
        required
        isClearable={false}
        onChange={handleChangeLimitType}
      />
      {interestLevel.limitType === MdlIndicationLimitType.Price ? (
        <StyledLimitPriceCurrencyInputField {...baseProps} name={`${baseName}.limitPrice`} />
      ) : (
        <StyledLimitPricePercentInputField name={`${baseName}.limitPercentage`} precision={0} />
      )}
    </StyledLimitPriceContainer>
  );
};

export default LimitPriceInput;
